// Theme registry (WEB_V2_BUILD.md §4). Slots: sectionContainer, webButton,
// heroContent, card, hero, nav, footer, plus tokens.
//
// - glass, flat, nightwatch keep their own component folders (kept as they
//   are; Flat must render pixel-identical).
// - Every other new theme renders the shared token-driven slots in ./base.
// - Old theme names (brutalist, neon, …) are aliases to their new theme;
//   their folders stay as "classic-<name>" for the 30-day switch back.
import {defineAsyncComponent, defineComponent, h} from "vue";
import glass from "./glass/index.js";
import flat from "./flat/index.js";
import nightwatch from "./nightwatch/index.js";
import brutalist from "./brutalist/index.js";
import neon from "./neon/index.js";
import corporate from "./corporate/index.js";
import album from "./album/index.js";
import paws from "./paws/index.js";
import bistro from "./bistro/index.js";
import noir from "./noir/index.js";
import workwear from "./workwear/index.js";
import shopfront from "./shopfront/index.js";
import base from "./base/index.js";
import {canonicalTheme, isKnownTheme, themeTokens, DEFAULT_THEME} from "./tokens.js";
import {usesLegacyHero} from "./layouts.js";

export {DEFAULT_THEME};

const LEGACY = {glass, flat, nightwatch};
const CLASSIC_FOLDERS = {brutalist, neon, corporate, album, paws, bistro, noir, workwear, shopfront};

const LegacyNav = defineAsyncComponent(() => import("../components/LegacyNavBar.vue"));
const LegacyFooter = defineAsyncComponent(() => import("../components/LegacyFooter.vue"));

// A legacy theme's hero: its own for the full layout, the shared hero when a
// section picks another layout the theme allows.
function heroSwitch(themeKey, legacyHero) {
    return defineComponent({
        name: "ThemeHeroSwitch",
        inheritAttrs: false,
        props: {layout: {type: String, default: ""}},
        setup(props, {attrs, slots}) {
            return () => h(usesLegacyHero(themeKey, props.layout) ? legacyHero : base.hero, {...attrs, layout: props.layout}, slots);
        },
    });
}

const legacyManifest = (key, folder) => ({
    ...glass,
    ...folder,
    hero: heroSwitch(key, folder.hero || glass.hero),
    nav: LegacyNav,
    footer: LegacyFooter,
    tokens: {...glass.tokens, ...(folder.tokens || {})},
});

const MANIFESTS = {
    glass: legacyManifest("glass", glass),
    flat: legacyManifest("flat", flat),
    nightwatch: legacyManifest("nightwatch", nightwatch),
    ...Object.fromEntries(Object.entries(CLASSIC_FOLDERS).map(([k, m]) => [`classic-${k}`, legacyManifest(`classic-${k}`, m)])),
};

function manifestFor(themeName) {
    const key = canonicalTheme(themeName);
    return MANIFESTS[key] || {...base, tokens: glass.tokens};
}

// Old callers (previewBridge validates studio messages with it).
export function isTheme(themeName) {
    return isKnownTheme(themeName);
}

// A theme only needs to define the slots it overrides; anything it omits
// falls back to the shared slot.
export function resolveThemeComponent(themeName, slot) {
    const manifest = manifestFor(themeName);
    return manifest[slot] || base[slot] || glass[slot];
}

// Theme tokens for the page root: legacy manifests' --theme-* tokens (as
// before) plus the --t-* contract every new section reads.
export function resolveThemeTokens(themeName, brandColors = {}, look = null) {
    const manifest = manifestFor(themeName);
    return {...glass.tokens, ...(manifest.tokens || {}), ...themeTokens(themeName, brandColors, look)};
}
